import { Injectable } from '@angular/core';
import { Group, GroupService } from '@services/member';
import { Label, LanguageService } from '@services/public';
import { Select, toVoidPromise } from '@util';
import { ChartConfiguration } from 'chart.js';
import { uuid } from 'jakapa-utilities-lite';
import { Observable } from 'rxjs';

export interface LeaderReportAPIResponseSelfByGroup {
	groupUUID: uuid;
	score: number;
}

@Injectable({
	providedIn: 'root',
})
export class LeaderReportSelfByGroupService {
	// Page langauge.
	labels: { [key in Label]?: string } = {
		[Label.SCORE]: '',
		[Label.SKILL_LEVEL]: '',
		[Label.UNKNOWN_GROUP]: '',
	};

	constructor(
		private _groupSvc: GroupService,
		private _languageSvc: LanguageService,
	) {
		// Get page language.
		this._languageSvc.getLabels(this.labels);
	}

	async generateReport(
		data: Array<LeaderReportAPIResponseSelfByGroup>,
		viewAllSelect: Select<boolean | null>,
	): Promise<ChartConfiguration | null> {
		// Check if viewAll needs to be toggled.
		if (viewAllSelect.value === null && data.length > 6) {
			viewAllSelect.value = false;
			return null;
		}
		if (viewAllSelect.value === false && data.length <= 6) {
			viewAllSelect.value = null;
			return null;
		}

		// Get groups list.
		let groups: Array<Group> = [];
		const apiCall: Observable<Array<Group>> = this._groupSvc.getGroupsAPI();
		apiCall.subscribe((res: Array<Group>) => (groups = res));
		await toVoidPromise(apiCall);

		// Format report data.
		const labels: Array<string> = [];
		const selfData: Array<number> = [];
		for (const groupData of data) {
			labels.push(
				groups.find((group) => group.uuid === groupData.groupUUID)?.name ||
					this.labels[Label.UNKNOWN_GROUP],
			);
			selfData.push(groupData.score);
		}

		// Truncate data.
		if (viewAllSelect.value === false) {
			labels.splice(6, Infinity);
			selfData.splice(6, Infinity);
		}

		return {
			type: 'bar',
			data: {
				labels,
				datasets: [
					{
						label: this.labels[Label.SCORE],
						data: selfData,
						borderRadius: 5,
						backgroundColor: '#3CE986',
					},
				],
			},
			options: {
				plugins: {
					legend: {
						display: true,
						position: 'top',
						align: 'end',
						labels: {
							useBorderRadius: true,
							borderRadius: 5.5,
							boxWidth: 10,
							boxHeight: 10,
						},
					},
				},
				responsive: true,
				maintainAspectRatio: false,
				indexAxis: 'y',
				scales: {
					y: {
						grid: {
							display: false,
						},
					},
					x: {
						min: 0,
						max: 5,
						ticks: {
							stepSize: 0.5,
						},
						title: {
							display: true,
							text: this.labels[Label.SKILL_LEVEL],
							color: '#757575',
						},
					},
				},
			},
		};
	}
}
